import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { socialApi, SocialWeek, Streak, WeekContentResponse } from '../services/api';
import Swal from 'sweetalert2';
import styles from './Social.module.css';

type PlatformKey = keyof SocialWeek['platforms'];

const PLATFORMS: { key: PlatformKey; label: string; icon: string }[] = [
  { key: 'facebook', label: 'Facebook', icon: '📘' },
  { key: 'instagram', label: 'Instagram', icon: '📷' },
  { key: 'linkedin', label: 'LinkedIn', icon: '💼' },
  { key: 'substack', label: 'Substack', icon: '📰' },
  { key: 'tiktok', label: 'TikTok', icon: '🎵' },
  { key: 'email', label: 'Email', icon: '📧' },
];

function formatWeekRange(weekStart: string, weekEnd: string): string {
  const start = new Date(weekStart);
  const end = new Date(weekEnd);
  const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
}

export default function Social() {
  const { getIdToken } = useAuth();
  const [week, setWeek] = useState<SocialWeek | null>(null);
  const [streak, setStreak] = useState<Streak | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contentLoading, setContentLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) {
        setError('Not authenticated');
        return;
      }

      const [weekRes, streakRes] = await Promise.all([
        socialApi.getCurrentWeek(token),
        socialApi.getStreak(token),
      ]);

      setWeek(weekRes.week);
      setStreak(streakRes.streak);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }

  async function handlePlatformToggle(platform: PlatformKey) {
    if (!week) return;

    const currentState = week.platforms[platform];
    const newPosted = !currentState.posted;

    try {
      const token = await getIdToken();
      if (!token) return;

      // If checking off, optionally ask for URL
      let url: string | null = currentState.url;
      if (newPosted && platform !== 'email') {
        const result = await Swal.fire({
          title: `${PLATFORMS.find(p => p.key === platform)?.label} Posted`,
          input: 'url',
          inputLabel: 'Post URL (optional)',
          inputPlaceholder: 'https://...',
          inputValue: url || '',
          showCancelButton: true,
          confirmButtonText: 'Save',
          cancelButtonText: 'Skip URL',
          showDenyButton: false,
        });

        if (result.isDismissed && result.dismiss === Swal.DismissReason.backdrop) {
          return; // User clicked outside, cancel the action
        }

        url = result.value || null;
      }

      const response = await socialApi.updatePlatform(token, week.id, platform, {
        posted: newPosted,
        url,
      });

      setWeek(response.week);

      // Reload streak if week is now complete
      if (response.week.isComplete) {
        const streakRes = await socialApi.getStreak(token);
        setStreak(streakRes.streak);
        Swal.fire({
          icon: 'success',
          title: 'Week Complete!',
          text: `All platforms posted. Streak: ${streakRes.streak.currentStreak} weeks!`,
          timer: 2000,
          showConfirmButton: false,
        });
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err instanceof Error ? err.message : 'Failed to update',
      });
    }
  }

  async function handleChannelFlowToggle(field: 'talkUploaded' | 'contentGenerated') {
    if (!week) return;

    try {
      const token = await getIdToken();
      if (!token) return;

      const response = await socialApi.updateChannelFlow(token, week.id, {
        [field]: !week.channelFlow[field],
      });
      setWeek(response.week);
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err instanceof Error ? err.message : 'Failed to update',
      });
    }
  }

  async function handleShortsCountChange() {
    if (!week) return;

    const result = await Swal.fire({
      title: 'Shorts Ready',
      input: 'number',
      inputLabel: 'How many shorts are ready?',
      inputValue: week.channelFlow.shortsCount,
      inputAttributes: {
        min: '0',
        step: '1',
      },
      showCancelButton: true,
      confirmButtonText: 'Save',
    });

    if (result.isConfirmed && result.value !== undefined) {
      try {
        const token = await getIdToken();
        if (!token) return;

        const response = await socialApi.updateChannelFlow(token, week.id, {
          shortsCount: parseInt(result.value, 10) || 0,
        });
        setWeek(response.week);
      } catch (err) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err instanceof Error ? err.message : 'Failed to update',
        });
      }
    }
  }

  async function showContentModal() {
    if (!week) return;

    setContentLoading(true);
    try {
      const token = await getIdToken();
      if (!token) return;

      const content: WeekContentResponse = await socialApi.getWeekContent(token, week.id);

      if (!content.sourceContent) {
        Swal.fire({
          icon: 'info',
          title: 'No Content Available',
          text: 'No published video content found in Channel Flow yet.',
        });
        return;
      }

      const { sourceContent } = content;

      // Build the modal HTML
      const platformSections = [
        { key: 'facebook', label: 'Facebook', title: sourceContent.facebookTitle, content: sourceContent.facebook },
        { key: 'linkedin', label: 'LinkedIn', title: sourceContent.linkedinTitle, content: sourceContent.linkedin },
        { key: 'substack', label: 'Substack', title: sourceContent.substackTitle, content: sourceContent.substack },
        { key: 'email', label: 'Email Newsletter', title: sourceContent.emailTitle, content: sourceContent.email },
      ]
        .filter(p => p.content)
        .map(p => `
          <div class="${styles.contentSection}">
            <h4>${p.label}</h4>
            ${p.title ? `<p class="${styles.contentTitle}">${escapeHtml(p.title)}</p>` : ''}
            <div class="${styles.contentText}">${escapeHtml(p.content || '').replace(/\n/g, '<br>')}</div>
            <button class="swal2-styled copy-btn" data-platform="${p.key}" style="background:#3b82f6;padding:6px 12px;font-size:13px;margin-top:8px;">
              Copy
            </button>
          </div>
        `)
        .join('');

      const quotesSection = sourceContent.quoteVisuals.length > 0
        ? `<div class="${styles.contentSection}">
            <h4>Quote Visuals (${sourceContent.quoteVisuals.length})</h4>
            <ul class="${styles.quoteList}">
              ${sourceContent.quoteVisuals.map(q => `<li>"${escapeHtml(q.quote)}"</li>`).join('')}
            </ul>
          </div>`
        : '';

      const shortsSection = sourceContent.shortsCandidates.length > 0
        ? `<div class="${styles.contentSection}">
            <h4>Shorts Candidates (${sourceContent.shortsCandidates.length})</h4>
            <ul class="${styles.shortsList}">
              ${sourceContent.shortsCandidates.map(s => `
                <li>
                  <strong>${escapeHtml(s.title)}</strong>
                  <span class="${styles.timestamp}">${formatTimestamp(s.start_time)} - ${formatTimestamp(s.end_time)}</span>
                  ${s.hook ? `<p class="${styles.hook}">${escapeHtml(s.hook)}</p>` : ''}
                </li>
              `).join('')}
            </ul>
          </div>`
        : '';

      const videoInfo = content.channelFlowRef
        ? `<p class="${styles.videoRef}">Video ID: ${content.channelFlowRef.videoId}</p>`
        : '';

      await Swal.fire({
        title: 'Social Content',
        html: `
          <div class="${styles.contentModal}">
            ${videoInfo}
            ${platformSections}
            ${quotesSection}
            ${shortsSection}
          </div>
        `,
        width: '700px',
        showCloseButton: true,
        showConfirmButton: false,
        didOpen: () => {
          // Attach copy handlers
          document.querySelectorAll('.copy-btn').forEach(btn => {
            btn.addEventListener('click', () => {
              const platform = btn.getAttribute('data-platform');
              const contentMap: Record<string, string | null> = {
                facebook: sourceContent.facebook,
                linkedin: sourceContent.linkedin,
                substack: sourceContent.substack,
                email: sourceContent.email,
              };
              const text = contentMap[platform || ''];
              if (text) {
                navigator.clipboard.writeText(text);
                (btn as HTMLElement).textContent = 'Copied!';
                setTimeout(() => {
                  (btn as HTMLElement).textContent = 'Copy';
                }, 1500);
              }
            });
          });
        },
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err instanceof Error ? err.message : 'Failed to load content',
      });
    } finally {
      setContentLoading(false);
    }
  }

  // Helper to escape HTML
  function escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Helper to format timestamp (seconds) to MM:SS
  function formatTimestamp(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading social tracker...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error}</div>
        <button onClick={loadData} className={styles.retryBtn}>
          Retry
        </button>
      </div>
    );
  }

  if (!week) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>No week data available</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1>Social Tracker</h1>
          <p className={styles.subtitle}>
            Week {week.id} ({formatWeekRange(week.weekStart, week.weekEnd)})
          </p>
        </div>
        {streak && (
          <div className={styles.streakBadge}>
            <span className={styles.streakIcon}>🔥</span>
            <span className={styles.streakCount}>{streak.currentStreak}</span>
            <span className={styles.streakLabel}>week streak</span>
          </div>
        )}
      </header>

      {/* Channel Flow Section */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Channel Flow</h2>
        <div className={styles.channelFlow}>
          <label className={styles.checkItem}>
            <input
              type="checkbox"
              checked={week.channelFlow.talkUploaded}
              onChange={() => handleChannelFlowToggle('talkUploaded')}
            />
            <span className={styles.checkLabel}>Talk Uploaded?</span>
            {week.channelFlow.uploadedAt && (
              <span className={styles.timestamp}>
                {new Date(week.channelFlow.uploadedAt).toLocaleDateString()}
              </span>
            )}
          </label>

          <label className={styles.checkItem}>
            <input
              type="checkbox"
              checked={week.channelFlow.contentGenerated}
              onChange={() => handleChannelFlowToggle('contentGenerated')}
            />
            <span className={styles.checkLabel}>Content Generated?</span>
            {week.channelFlow.generatedAt && (
              <span className={styles.timestamp}>
                {new Date(week.channelFlow.generatedAt).toLocaleDateString()}
              </span>
            )}
          </label>

          <button className={styles.shortsBtn} onClick={handleShortsCountChange}>
            <span className={styles.shortsCount}>{week.channelFlow.shortsCount}</span>
            <span className={styles.shortsLabel}>shorts ready</span>
          </button>

          <button
            className={styles.contentBtn}
            onClick={showContentModal}
            disabled={contentLoading}
          >
            {contentLoading ? 'Loading...' : 'View Content'}
          </button>
        </div>
      </section>

      {/* Platforms Checklist */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Platforms</h2>
          <div className={styles.completionBadge}>
            <span className={week.isComplete ? styles.complete : ''}>
              {week.completionCount}/{week.totalPlatforms} posted
            </span>
          </div>
        </div>

        <div className={styles.platformGrid}>
          {PLATFORMS.map(({ key, label, icon }) => {
            const platform = week.platforms[key];
            return (
              <div
                key={key}
                className={`${styles.platformCard} ${platform.posted ? styles.posted : ''}`}
                onClick={() => handlePlatformToggle(key)}
              >
                <div className={styles.platformCheck}>
                  <input
                    type="checkbox"
                    checked={platform.posted}
                    onChange={() => {}}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
                <span className={styles.platformIcon}>{icon}</span>
                <span className={styles.platformLabel}>{label}</span>
                {platform.posted && platform.postedAt && (
                  <span className={styles.platformTime}>
                    {new Date(platform.postedAt).toLocaleDateString()}
                  </span>
                )}
                {platform.posted && platform.url && (
                  <a
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.platformLink}
                    onClick={(e) => e.stopPropagation()}
                  >
                    View
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Streak Info */}
      {streak && streak.longestStreak > 0 && (
        <section className={styles.streakInfo}>
          <p>
            Longest streak: <strong>{streak.longestStreak} weeks</strong>
          </p>
          {streak.lastCompletedWeek && (
            <p className={styles.lastCompleted}>
              Last completed: {streak.lastCompletedWeek}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
